import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

import { constants } from '../../../config';
import { ScreenStore } from '../../../stores/ScreenStore';
import { enforceParameters } from '../../../hocs/enforce-parameters';
import { ProductStore } from '../stores/ProductStore';
import { ConfigParametersStore } from '../../../stores/ConfigParametersStore';
import { NumberHelper } from '../../../helpers/NumberHelper';
import styles from './FinancingSection.css';

const AMOUNT_REGEX = /^\$\d*$/i;

const IPropTypes = {
  reference: PropTypes.func,
};

const defaultProps = {
  reference: () => {},
};

class FinancingSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      financingAmount: null,
      listExpandedMobile: false,
    };
    this._expandList = this._expandList.bind(this);
    this._onAmountChange = this._onAmountChange.bind(this);
  }

  static renderFee(fee) {
    return FinancingSection.renderFeeWithInitialAmount(fee, 0);
  }

  static renderFeeWithInitialAmount(fee, initialAmount) {
    let stepPriceString = fee.priceString;
    let stepPrice = fee.price;
    let ptfString = fee.totalPriceString;
    if (initialAmount > 0) {
      stepPrice = Math.round((fee.totalPrice - initialAmount) / fee.amount);
      stepPriceString = NumberHelper.localizeNumber(stepPrice);
      ptfString = NumberHelper.localizeNumber(stepPrice * fee.amount);
    }
    return (
      <tr key={`${fee.price}-${fee.amount}`}>
        <td>
          <p className={styles.financingAmounts}>
            ${stepPriceString}
          </p>
        </td>
        <td>
          <p className={styles.financingAmounts}>
            x
          </p>
        </td>
        <td>
          <p className={styles.financingAmounts}>
            {fee.amount} cuotas
          </p>
        </td>
        <td>
          <p className={styles.financingTotalPrice}>
            PTF ${ptfString}
          </p>
        </td>
      </tr>
    );
  }

  componentDidMount() {
    const minimumPercentage = ConfigParametersStore.parameters.minimumFinancingPercentage;
    const minimumAmount = ProductStore.product.financing.price * (minimumPercentage / 100);
    ProductStore.fetchFinancingForProduct(Math.ceil(minimumAmount));
  }

  _onAmountChange(event) {
    const { target: { value } } = event;
    if (AMOUNT_REGEX.test(value)) {
      const numberString = value.slice(1);
      let newValue = -1;
      if (numberString) {
        newValue = Number(numberString);
      }
      if (newValue <= Number.MAX_SAFE_INTEGER) {
        ProductStore.fetchFinancingForProduct(newValue);
        this.setState({
          financingAmount: newValue,
        });
      }
    }
  }

  _expandList() {
    this.setState({
      listExpandedMobile: true,
    });
  }

  render() {
    const { product: { financing } } = ProductStore;
    const {
      zeroDayExpiry,
      thirtyDayExpiry,
    } = financing;
    const withInitialPayment = ProductStore.financingWithInitialPayment;
    let { financingAmount } = this.state;
    const minimumPercentage = ConfigParametersStore.parameters.minimumFinancingPercentage;
    let minimumAmount = ProductStore.product.financing.price * (minimumPercentage / 100);
    minimumAmount = Math.ceil(minimumAmount);
    if (financingAmount === null) {
      financingAmount = minimumAmount;
    }
    const showMobileVersion = ScreenStore.screenWidth <= constants.breakpoints.smallMaxWidth;
    return (
      <section ref={this.props.reference}>
        {
          !showMobileVersion &&
          <h2 className={styles.title}>
            Financiamiento
          </h2>
        }
        {
          showMobileVersion &&
          <h2 className={styles.mobileTitle}>
            Opciones de financiamiento
          </h2>
        }
        <div className={styles.financingBox}>
          <div className={styles.financingDescription}>
            <h4 className={styles.financingTitle}>
              Planes en cuotas
            </h4>
            <p className={styles.financingSubtext}>
              Pagando la primera cuota al momento de la compra.
            </p>
          </div>
          <div className={styles.financingList}>
            <table>
              <tbody>
                {zeroDayExpiry.map(FinancingSection.renderFee)}
              </tbody>
            </table>
          </div>
        </div>
        {
          (!showMobileVersion || this.state.listExpandedMobile) &&
          <div className={styles.financingBox}>
            <div className={styles.financingDescription}>
              <h4 className={styles.financingTitle}>
                Planes con entrega diferida
              </h4>
              <p className={styles.financingSubtext}>
                La primera cuota se paga a los 30 días de efectuada la compra.
              </p>
            </div>
            <div className={styles.financingList}>
              <table>
                <tbody>
                  {thirtyDayExpiry.map(FinancingSection.renderFee)}
                </tbody>
              </table>
            </div>
          </div>
        }
        {
          (!showMobileVersion || this.state.listExpandedMobile) &&
          <div className={styles.financingBox}>
            <div className={styles.financingDescription}>
              <h4 className={styles.financingTitle}>
                Planes con entrega inicial
              </h4>
              <p className={styles.financingSubtext}>
                Pagando un monto inicial de:
                <input
                  className={styles.amountInput}
                  onChange={this._onAmountChange}
                  value={`$${financingAmount === -1 ? '' : financingAmount}`}
                />
              </p>
            </div>
            <div className={styles.financingList}>
              {
                financingAmount > financing.price &&
                <p className={styles.errorMessage}>
                  El monto inicial no puede ser mayor al precio total
                  del producto
                </p>
              }
              {
                financingAmount >= 0 && financingAmount < minimumAmount &&
                <p className={styles.errorMessage}>
                  El monto inicial no puede ser menor al monto mínimo de ${minimumAmount}
                </p>
              }
              {
                withInitialPayment &&
                financingAmount >= minimumAmount &&
                financingAmount < financing.price &&
                <table>
                  <tbody>
                    { withInitialPayment.map(fee =>
                      FinancingSection.renderFeeWithInitialAmount(fee, financingAmount))
                    }
                  </tbody>
                </table>
              }
            </div>
          </div>
        }
        {
          showMobileVersion && !this.state.listExpandedMobile &&
          <button
            className={styles.seeAllButton}
            onClick={this._expandList}
          >
            Ver todos los planes financiados ›
          </button>
        }
      </section>
    );
  }
}

FinancingSection.propTypes = IPropTypes;
FinancingSection.defaultProps = defaultProps;

const FinancingSectionWrapper = enforceParameters(observer(FinancingSection));

export { FinancingSectionWrapper as FinancingSection };



// WEBPACK FOOTER //
// ./src/pages/product-detail/financing-section/FinancingSection.jsx